import React, { useEffect, useState, useContext } from "react";
import {
  Box,
  Stack,
  Divider,
  SelectChangeEvent,
  Container,
  Button,
  IconButton,
  Typography,
  List,
  ListItem,
} from "@mui/material";

import { doesPermissionsHaveSomeOf } from "../../utils/authUtils";
import { AuthContext } from "../../context/auth-context/AuthContext";

import TraineeSectionTable from "../../components/members/trainees/TraineeSectionTable";
import InternalProjectCard from "../../components/members/trainees/InternalProjectCard";
import TraineeSectionSkeleton from "../../components/members/trainees/TraineeSectionSkeleton";
import GenerationSelection from "../../components/members/trainees/GenerationSelection";
import useMembers from "../../hooks/members/useMembers";
import useTrainees from "../../hooks/trainees/useTrainees";
import useTraineeGeneration from "../../hooks/trainees/useTraineeGeneration";
import AddInternalProjectDialog from "../../components/members/trainees/AddInternalProjectDialog";
import { AddCircle } from "@mui/icons-material";
import useResponsive from "../../hooks/useResponsive";
import { Link } from "react-router-dom";
import { GenerationDto } from "../../types/traineesTypes";
import LoadingTable from "../../components/general/LoadingTable";

/**
 * This component displays the trainee section page
 * All internal projects of the selected generation are displayed as cards
 * If the user has the permission to add internal projects, a button is displayed to add a new internal project
 * All trainees of the selected generation are displayed in a table if the user has the permission to see the trainees
 * @returns TraineeSection
 */
const TraineeSection: React.FunctionComponent = () => {
  const { auth } = useContext(AuthContext);
  const { generations, trainees, isGenerationsFetched, admitTrainee, createInternalProject } = useTrainees();
  const { members } = useMembers();

  const isMobile = useResponsive("down", "sm");

  const [selectedGeneration, setSelectedGeneration] = useState<GenerationDto | null>(null);
  const hasPermissionInternalProject = doesPermissionsHaveSomeOf(auth.permissions, [15]);

  const [isCreateIPDialogOpen, setIsCreateIPDialogOpen] = useState(false);

  const {
    traineeProgress,
    isTraineeProgressFetched,
    internalProjectsOfGeneration,
    isInternalProjectsOfGenerationFetched,
    mandatoryWorkshops,
  } = useTraineeGeneration(selectedGeneration?.generationId || 0);

  useEffect(() => {
    if (isGenerationsFetched && generations.length > 0) {
      setSelectedGeneration(generations[0]);
    }
  }, [isGenerationsFetched]);

  // handles the change of the selected generation
  const handleGenerationChange = (event: SelectChangeEvent<number | null>): void => {
    const selectedGenerationId = event.target.value as number;
    setSelectedGeneration(generations.find((generation) => generation.generationId === selectedGenerationId) || null);
  };

  // trainees of the selected generation that are not assigned to an internal project
  const traineesWithoutIP = members
    // filter for the selected generation
    .filter((member) => member.generationId === selectedGeneration?.generationId)
    // filter for trainees
    .filter((member) => member.memberStatus?.name !== "Ausgetretene" && member.memberStatus?.name === "Trainee")
    // filter for trainees that are not assigned to an internal project
    .filter(
      (trainee) =>
        !internalProjectsOfGeneration.some((ip) => ip.members?.some((member) => member.memberId === trainee.memberId))
    );

  return !isGenerationsFetched || !isInternalProjectsOfGenerationFetched ? (
    <Container maxWidth="lg">
      <TraineeSectionSkeleton />
    </Container>
  ) : (
    <Container maxWidth="lg">
      {hasPermissionInternalProject && selectedGeneration ? (
        <AddInternalProjectDialog
          open={isCreateIPDialogOpen}
          handleDialogClose={() => setIsCreateIPDialogOpen(false)}
          addInternalProject={createInternalProject}
          generationId={selectedGeneration.generationId}
          generationName={selectedGeneration.description}
          members={members}
          trainees={trainees}
        />
      ) : null}
      <Stack sx={{ mt: 1, mb: 3 }} direction={"row"} spacing={5} alignItems={"end"}>
        <GenerationSelection
          selectedGeneration={selectedGeneration}
          handleGenerationChange={handleGenerationChange}
          generations={generations}
        />
        {generations[0]
          ? hasPermissionInternalProject &&
            selectedGeneration?.generationId === generations[0].generationId &&
            (!isMobile ? (
              <Button
                variant="contained"
                startIcon={<AddCircle />}
                onClick={() => setIsCreateIPDialogOpen(true)}
                sx={{ height: 40 }}
              >
                Internes Projekt Hinzufügen
              </Button>
            ) : (
              <IconButton onClick={() => setIsCreateIPDialogOpen(true)} sx={{ height: 40 }}>
                <AddCircle color="primary" sx={{ width: 40, height: 40 }} />
              </IconButton>
            ))
          : null}
      </Stack>
      <Box
        sx={() => ({
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          justifyContent: "left",
          overflowX: { sm: "auto" },
          flexWrap: "wrap",
        })}
      >
        {internalProjectsOfGeneration.map((internalProject) => (
          <InternalProjectCard internalProject={internalProject} />
        ))}
      </Box>

      {hasPermissionInternalProject ? (
        !isTraineeProgressFetched ? (
          <LoadingTable />
        ) : (
          <Box sx={{ pb: 5 }}>
            <Divider sx={{ mb: 5 }} />
            {traineesWithoutIP.length > 0 ? (
              <Box>
                <Typography fontSize={20} fontWeight={"bold"}>
                  Trainees ohne IP
                </Typography>
                <List sx={{ mb: 1, listStyleType: "disc", pl: 4 }}>
                  {traineesWithoutIP.map((trainee) => (
                    <ListItem disableGutters disablePadding key={trainee.memberId} sx={{ display: "list-item" }}>
                      <Link
                        to={`/gesamtuebersicht/${trainee.memberId}`}
                        style={{ textDecoration: "none", color: "black" }}
                      >
                        {trainee.firstname} {trainee.lastname}
                      </Link>
                    </ListItem>
                  ))}
                </List>
              </Box>
            ) : null}
            <TraineeSectionTable
              trainees={traineeProgress}
              admitTrainee={admitTrainee}
              mandatoryWorkshops={mandatoryWorkshops}
            />
          </Box>
        )
      ) : null}
    </Container>
  );
};

export default TraineeSection;
